import { alpha, Theme } from '@mui/material/styles';

const validationColour = '#E7000B';
const inputLineHeight = 1.4375;
const focusBorderWidth = 2;
const borderWidth = 1;

const resolveTokens = (theme: Theme) => {
    const { fontSize, htmlFontSize, fontFamily, fontWeightMedium, caption } = theme.typography;
    // Dojo needs concrete pixels, so the theme's spacing strings are read back
    // out as numbers.
    const spacing = (units: number) => parseFloat(theme.spacing(units));
    const remToPx = (size: string | number | undefined) =>
        Math.round(parseFloat(String(size)) * htmlFontSize);

    const lineHeight = Math.round(fontSize * inputLineHeight);
    const paddingY = spacing(1);
    const paddingX = spacing(1.5);
    const text = theme.palette.text.primary;

    return {
        text,
        fontFamily,
        fontSize,
        lineHeight,
        fontWeightMedium,
        paddingY,
        paddingX,
        background: theme.palette.background.paper,
        border: alpha(text, 0.23),
        focusBorder: theme.palette.primary.main,
        radius: `${theme.shape.borderRadius}px`,
        gap: theme.spacing(2),
        labelGap: theme.spacing(0.75),
        validationGap: theme.spacing(0.5),
        iconGap: theme.spacing(1),
        validationFontSize: remToPx(caption.fontSize),
        validationLineHeight: Math.round(remToPx(caption.fontSize) * Number(caption.lineHeight)),
        transition: theme.transitions.create(['border-color', 'color'], {
            duration: theme.transitions.duration.shortest
        }),
        height: lineHeight + paddingY * 2 + borderWidth * 2
    };
};

const fullRow = { flex: '1 1 100%' };
const halfRow = { flex: '1 1 0', 'min-width': '0' };

export const createDojoStyles = (theme: Theme): Dojo.Styles => {
    const token = resolveTokens(theme);
    const cardIconHeight = token.lineHeight;
    return {
        base: {
            default: {
                '-webkit-appearance': 'none',
                '-moz-appearance': 'none',
                appearance: 'none',
                width: '100%',
                color: token.text,
                backgroundColor: 'transparent',
                textDecoration: 'none',
                fontFamily: token.fontFamily,
                boxSizing: 'border-box',
                boxShadow: 'none',
                fontSize: `${token.fontSize}px`,
                height: `${token.height}px`,
                padding: `${token.paddingY}px ${token.paddingX}px`,
                borderRadius: token.radius,
                borderColor: token.border,
                lineHeight: `${token.lineHeight}px`,
                margin: '0',
                borderWidth: `${borderWidth}px`,
                transition: token.transition
            },
            focus: {
                borderColor: token.focusBorder,
                borderWidth: `${focusBorderWidth}px`,
                padding: `${token.paddingY - 1}px ${token.paddingX - 1}px`,
                outline: '0'
            },
            error: {
                borderColor: validationColour,
                borderWidth: `${borderWidth}px`
            },
            valid: {},
            label: {
                display: 'block',
                fontSize: `${token.fontSize}px`,
                lineHeight: `${token.lineHeight}px`,
                fontWeight: `${token.fontWeightMedium}`,
                marginBottom: token.labelGap,
                color: token.text,
                fontFamily: token.fontFamily
            },
            validationText: {
                fontSize: `${token.validationFontSize}px`,
                lineHeight: `${token.validationLineHeight}px`,
                fontWeight: '400',
                marginTop: token.validationGap,
                color: validationColour,
                fontFamily: token.fontFamily
            },
            validationIcon: {
                width: '16px',
                height: '18px',
                marginTop: '1px',
                marginRight: token.iconGap,
                verticalAlign: 'top'
            }
        },
        form: {
            display: 'flex',
            'flex-wrap': 'wrap',
            'align-items': 'flex-start',
            gap: token.gap,
            'background-color': token.background,
            'font-family': token.fontFamily
        },
        cardName: {
            container: { ...fullRow, order: '1' }
        },
        cardNumber: {
            container: { ...fullRow, order: '2' }
        },
        expiryDate: {
            container: { ...halfRow, order: '3' }
        },
        cv2: {
            container: { ...halfRow, order: '4' }
        },
        cardIcon: {
            height: `${cardIconHeight}px`,
            bottom: `${(token.height - cardIconHeight) / 2}px`
        }
    };
};
